import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AuthState, User } from "../../../interfaces/screens/auth/login";
import { TabWithCategories } from "../../../interfaces/api/chat/config";
import { Tab } from "../../../interfaces/api/congresses";

const initialState: AuthState = {
  currentUser: null,
  categories: [],
  tabs: [],
  isLoadingCategories: true,
  isLoadingTabs: true,
};

/**
 * Redux slice for managing loading state.
 */
export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    /**
     * Action to set the current user.
     * @param {AuthState} state - Current auth state.
     * @param {PayloadAction<User>} action - Action payload containing the user.
     */
    setCurrentUser: (state: AuthState, action: PayloadAction<User>) => {
      state.currentUser = action.payload;
    },
    /**
     * Action to set the categories.
     * @param {AuthState} state - Current auth state.
     * @param {PayloadAction<TabWithCategories[]>} action - Action payload containing the categories.
     */
    setCategories: (
      state: AuthState,
      action: PayloadAction<TabWithCategories[]>,
    ) => {
      state.categories = action.payload;
      state.isLoadingCategories = false;
    },
    /**
     * Action to set the loading state for categories.
     * @param {AuthState} state - Current auth state.
     * @param {PayloadAction<boolean>} action - Action payload containing the loading state.
     */
    setLoadingCategories: (
      state: AuthState,
      action: PayloadAction<boolean>,
    ) => {
      state.isLoadingCategories = action.payload;
    },
    /**
     * Action to set the tabs.
     * @param {AuthState} state - Current auth state.
     * @param {PayloadAction<Tab[]>} action - Action payload containing the tabs.
     */
    setTabs: (state: AuthState, action: PayloadAction<Tab[]>) => {
      state.tabs = action.payload;
      state.isLoadingTabs = false;
    },
    /**
     * Action to set the loading state for tabs.
     * @param {AuthState} state - Current auth state.
     * @param {PayloadAction<boolean>} action - Action payload containing the loading state.
     */
    setLoadingTabs: (state: AuthState, action: PayloadAction<boolean>) => {
      state.isLoadingTabs = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setCurrentUser,
  setCategories,
  setLoadingCategories,
  setTabs,
  setLoadingTabs,
} = authSlice.actions;

export default authSlice.reducer;
